import AdvancedDestinationResults from './advanced-destination-results';
import AdvancedDestinationTabs from './advanced-destination-tabs';

/* global RoiCalendar */
export default class AdvancedDestinationSelector {
    constructor(form, destinationCodeInput) {
        this._form = form;
        this._container = this._initContainer();
        this._destinationPrefixes = JSON.parse(this._form.dataset.destinationPrefixes);
        this._destinationCodeInput = destinationCodeInput;
        this._destinations = this._formatDestinations(JSON.parse(this._container.dataset.destinations));
        this._mainInput = form.querySelector('.js-roi-search-engine-destination-trigger');
        this._overlay = RoiCalendar.getOverlay(this._close.bind(this));
        this._autocompleteField = this._container.querySelector('.js-roi-search-engine-destination-autocomplete');
        this._resultsListSelector = '.js-roi-search-engine-destination-results-list';
        this._visibleClass = 'roi-search-engine__destination--visible';
        this._destinationTabs = new AdvancedDestinationTabs(this._container);
        this._destinationResults = new AdvancedDestinationResults(
            this, this._container, this._destinationTabs
        );

        this._mainInput.addEventListener('focus', () => {
            this.filterOptions(true);
        });
        if (this._autocompleteField) {
            this._autocompleteField.addEventListener('input', () => {
                this.filterOptions();
            });
        }
        this._clearInput();
    }

    selectChoice(choice) {
        if (this._autocompleteField) {
            this._autocompleteField.value = choice.innerText;
        }
        if (choice.dataset.name === undefined) {
            this._mainInput.value = choice.innerHTML.split('<span')[0].replace(/(<([^>]+)>)/gi, '').trim();
        } else {
            this._mainInput.value = choice.dataset.name.replace(/(<([^>]+)>)/gi, '');
        }
        this._destinationCodeInput.value = choice.dataset.code;
        if (this._destinationPrefixes.includes(this._destinationCodeInput.value[0])) {
            const occupancyContainer = document.querySelector(
                '.js-roi-search-engine-occupancy-container'
            );

            if (occupancyContainer) {
                occupancyContainer.style.display = 'none';
            }
        } else {
            this._destinationCodeInput.dispatchEvent(new CustomEvent('input', {
                bubbles: true,
                cancelable: true
            }));
        }

        this._close();
    }

    _clear() {
        this._container.querySelectorAll(
            this._resultsListSelector
        ).forEach((list) => {
            list.innerHTML = '';
        });
    }

    _clearInput() {
        const clearButton = this._container.querySelector(
            '.js-roi-search-engine-destination-clear'
        );

        if (clearButton) {
            clearButton.addEventListener('click', () => {
                this._autocompleteField.value = '';
                this.filterOptions();
                this._autocompleteField.focus();
            });
        }
    }

    _close() {
        this._overlay.hide();
        this._container.classList.remove(this._visibleClass);
        this._form.querySelector('.js-roicalendar-trigger').focus();
    }

    filterOptions(openFlag = false) {
        this._clear();
        let enteredText = this._autocompleteField.value,
            choices;
        if (enteredText === '' || openFlag) {
            if (openFlag) {
                this._autocompleteField.value = '';
            }
            choices = { ...this._destinations };
        } else {
            enteredText = this.constructor._sanitizeString(enteredText);
            choices = {};
            Object.keys(this._destinations).forEach((key) => {
                choices[key] = this._destinations[key].filter(item => item.searchText.includes(enteredText));
            });
        }
        this._populateContainer(choices);
        this._autocompleteField.value = this._autocompleteField.value
            .replace(/^[\s-]+/g, '')
            .replace(/[^\p{L}\p{N} -]/gu, '');
        this._container.classList.add(this._visibleClass);
        const calendar = document.querySelector('#roicalendar');

        if (calendar.classList.contains('show-calendar') && calendar.classList.contains('no-modal')) {
            calendar.classList.remove('show-calendar');
        }
        this._overlay.show();
    }

    _formatDestinations(destinations) {
        const results = {
                destinations: [],
                hotels: []
            },
            hasSubtitle = this._container.dataset.destinationWithSubtitle;
        this._hotelTrans = this._container.dataset.transHotel;
        this._hotelsTrans = this._container.dataset.transHotels;

        destinations.forEach((city) => {
            this._cityName = false;
            this._isHotel = false;
            results.destinations.push(this._formatCity(city, hasSubtitle));
            city.hotels.forEach((hotel) => {
                results.hotels.push(this._formatHotel(hotel, city, hasSubtitle));
            });
        });
        return results;
    }

    _formatCity(city, subtitle) {
        if (subtitle !== undefined) {
            this._cityName = city.hotels.length === 1
                ? `1 ${this._hotelTrans}`
                : `${city.hotels.length} ${this._hotelsTrans}`;
        }
        return this._formatItem(city.name, city.code, `${city.code} - ${city.name}`);
    }

    _formatHotel(hotel, city, subtitle) {
        const hasCategory = this._container.dataset.destinationWithCategory;
        this._isHotel = true;
        if (subtitle !== undefined) {
            this._cityName = city.name;
        }

        if (hasCategory !== undefined) {
            this._hotelCategory = hotel[2]; // eslint-disable-line
        }

        return this._formatItem(
            hotel[1],
            hotel[0],
            `${city.code} - ${city.name} - ${hotel[0]} - ${hotel[1]}`
        );
    }

    _formatItem(name, code, searchText) {
        return {
            name: name,
            code: code,
            searchText: this.constructor._sanitizeString(searchText).replace(/([@#~])/g, ''),
            city: this._cityName,
            isHotel: this._isHotel,
            hotelCategory: this._hotelCategory
        };
    }

    _initContainer() {
        let container = (
            this._form.querySelector('.js-roi-search-engine-destination-selector')
        );
        if (container === null) {
            throw Error('Advanced destination selector not found');
        }

        container = container.parentNode.removeChild(container);
        document.body.appendChild(container);
        return container;
    }

    _populateContainer(choices) {
        ['all', 'destinations', 'hotels'].forEach((category) => {
            let index = 0;
            Object.keys(choices).forEach((key) => {
                if (category !== 'all' && key !== category) {
                    return;
                }
                const choiceGroupContainer = this._container.querySelector(
                        `${this._resultsListSelector}[data-category=${category}][data-key=${key}]`
                    ),
                    groupChoices = choices[key];
                let results;
                if (groupChoices.length === 0) {
                    results = this._destinationResults.createNoResultsMessage();
                } else {
                    [results, index] = this._getSortedResults(groupChoices, index, category);
                }
                choiceGroupContainer.appendChild(results);
            });
        });
        this._destinationResults.listenToResultsEvents();
    }

    _getSortedResults(groupChoices, index, category) {
        const fragment = document.createDocumentFragment(),
            results = [];
        groupChoices.forEach((choice) => {
            results.push(this._destinationResults.createResult(choice, category));
        });
        results.sort((first, second) => (getText(first) < getText(second) ? -1 : 1));
        results.forEach((sortedResult) => {
            sortedResult.dataset.index = index++;
            fragment.appendChild(sortedResult);
        });
        return [fragment, index];

        function getText(item) { return item.innerText.trim().toLowerCase(); }
    }

    static _sanitizeString(input) {
        return input.replace(/[áàäâåÁÀÄÂÅ]/g, 'a')
            .replace(/[éèëêÉÈËÊ]/g, 'e')
            .replace(/[íìïîÍÌÏÎ]/g, 'i')
            .replace(/[óòöôøÓÒÖÔØ]/g, 'o')
            .replace(/[úùüûÚÙÜÛ]/g, 'u')
            .replace(/[ñÑ]/g, 'n')
            .replace(/[çÇ]/g, 'c')
            .replace(/[æÆ]/g, 'ae')
            .toLowerCase();
    }
}
